import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import BEM from 'common/bem';

const classes = BEM.with('ResizableSidePane');

const KEYBOARD_STEP_PX = 16;
const STORAGE_KEY = 'tiger_agent_pane_width';

type ResizableSidePaneProps = {
  children: React.ReactNode;
  className?: string;
  defaultWidth: number;
  handleLabel: string;
  isOpen: boolean;
  maxWidth: number;
  minWidth: number;
};

export function clampWidth(width: number, minWidth: number, maxWidth: number): number {
  return Math.min(maxWidth, Math.max(minWidth, width));
}

function readStoredWidth(minWidth: number, maxWidth: number): number | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored === null ? NaN : Number.parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : clampWidth(parsed, minWidth, maxWidth);
  } catch {
    return null;
  }
}

function storeWidth(width: number) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    return;
  }
}

export default function ResizableSidePane({
  children,
  className,
  defaultWidth,
  handleLabel,
  isOpen,
  maxWidth,
  minWidth,
}: ResizableSidePaneProps) {
  const [width, setWidth] = useState(() => readStoredWidth(minWidth, maxWidth) ?? defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const stopResizingRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopResizingRef.current?.(), []);

  useEffect(() => {
    storeWidth(width);
  }, [width]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const { cursor, userSelect } = document.body.style;

    const onMouseMove = (moveEvent: MouseEvent) => {
      setWidth(clampWidth(startWidth - (moveEvent.clientX - startX), minWidth, maxWidth));
    };
    const stop = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('blur', stop);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
      stopResizingRef.current = null;
      setIsResizing(false);
    };

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('blur', stop);
    stopResizingRef.current = stop;
    setIsResizing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const delta =
      e.key === 'ArrowLeft' ? KEYBOARD_STEP_PX : e.key === 'ArrowRight' ? -KEYBOARD_STEP_PX : 0;
    if (!delta) return;
    e.preventDefault();
    setWidth((current) => clampWidth(current + delta, minWidth, maxWidth));
  };

  return (
    <div
      className={classNames(className, classes({ isResizing }))}
      style={
        isOpen
          ? { flexBasis: width, width, transition: isResizing ? 'none' : undefined }
          : undefined
      }
    >
      {isOpen && (
        <div
          aria-label={handleLabel}
          aria-orientation="vertical"
          aria-valuemax={maxWidth}
          aria-valuemin={minWidth}
          aria-valuenow={width}
          className={classes('handle')}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          role="separator"
          tabIndex={0}
          title="Drag to resize"
        />
      )}
      {children}
    </div>
  );
}
